import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Instagram, Twitter, Facebook, Youtube, Clock, ChevronRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.footer-reveal',
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: footerRef.current,
            start: "top 85%",
          }
        }
      );
    }, footerRef);
    return () => ctx.revert();
  }, []);

  return (
    <footer ref={footerRef} className="relative pt-24 pb-12 bg-[#010816] border-t-2 border-primary/20 shadow-2xl text-white overflow-hidden" id="footer">
      {/* Atmospheric Glow Elements */}
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[150px] pointer-events-none" />

      {/* Cinematic Overlays */}
      <div className="absolute inset-0 opacity-[0.02] bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/10 via-transparent to-transparent pointer-events-none opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="section-container relative z-10">
        <div className="footer-reveal grid lg:grid-cols-4 gap-12 border-b border-white/5 pb-12">
          <div className="space-y-10">
            <Link to="/" className="flex flex-col gap-2 group">
              <img 
                src="/images/logo-trasparent.png" 
                alt="Dr. Suresh Pet Care" 
                className="h-32 md:h-44 w-40 brightness-110 contrast-105 drop-shadow-[0_0_15px_rgba(255,255,255,0.1)] group-hover:scale-105 transition-all duration-500"
              />
            </Link>
            <p className="text-white/90 text-sm leading-relaxed pr-8 font-medium">
              A leading destination for comprehensive pet healthcare in Vanasthalipuram, committed to delivering quality medical and surgical care for pets.
            </p>
            <div className="space-y-6">
               <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-primary">Clinic Address</h4>
               <p className="text-white/90 text-sm font-bold">Vanasthalipuram, Hyderabad</p>
               <a href="mailto:dr.sureshhvet97@gmail.com" className="text-white/60 hover:text-primary transition-colors text-xs font-bold block truncate max-w-[200px]">dr.sureshhvet97@gmail.com</a>
            </div>
            <div className="space-y-6">
               <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-primary">Connect</h4>
               <div className="flex gap-4">
                  {[
                    { id: 'fb', Icon: Facebook, url: '#' },
                    { id: 'ig', Icon: Instagram, url: 'https://www.instagram.com/dr_sureshbabu' },
                    { id: 'tw', Icon: Twitter, url: '#' },
                    { id: 'yt', Icon: Youtube, url: '#' }
                  ].map((social) => (
                    <a key={social.id} href={social.url} target={social.url !== '#' ? "_blank" : "_self"} rel={social.url !== '#' ? "noopener noreferrer" : ""} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all duration-500">
                       <social.Icon size={16} />
                    </a>
                  ))}
               </div>
            </div>
          </div>

          <div className="space-y-10 pt-4">
             <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-white/80">Navigation</h4>
             <ul className="space-y-3">
                <li>
                   <Link to="/gallery" className="group flex items-center gap-3 text-white/80 hover:text-primary transition-all duration-500">
                      <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-sm shadow-primary/20">
                         <ChevronRight size={14} />
                      </span>
                      <span className="text-sm font-bold tracking-wide">Visual Gallery</span>
                   </Link>
                </li>
                <li>
                   <Link to="/blog" className="group flex items-center gap-3 text-white/80 hover:text-primary transition-all duration-500">
                      <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-sm shadow-primary/20">
                         <ChevronRight size={14} />
                      </span>
                      <span className="text-sm font-bold tracking-wide">Clinical Insights</span>
                   </Link>
                </li>
                <li>
                   <Link to="/contact" className="group flex items-center gap-3 text-white/80 hover:text-primary transition-all duration-500">
                      <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-sm shadow-primary/20">
                         <ChevronRight size={14} />
                      </span>
                      <span className="text-sm font-bold tracking-wide">Direct Contact</span>
                   </Link>
                </li>
                <li>
                   <Link to="/surgeries" className="group flex items-center gap-3 text-white/80 hover:text-primary transition-all duration-500">
                      <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-sm shadow-primary/20">
                         <ChevronRight size={14} />
                      </span>
                      <span className="text-sm font-bold tracking-wide">Surgical Excellence</span>
                   </Link>
                </li>
             </ul>
             
             <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-white/80">Legal Framework</h4>
             <ul className="space-y-4 text-white/90 text-sm font-bold">
                <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-primary transition-colors">Terms & Conditions</Link></li>
             </ul>
          </div>

          <div className="space-y-10 pt-4">
             <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-primary">Clinical Care</h4>
             <ul className="space-y-4 text-white/90 text-sm font-bold">
                <li className="hover:text-primary transition-colors cursor-pointer"><Link to="/internal-medicine">Internal Medicine</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/surgeries">Surgical Unit</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/oncology">Oncology Care</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/dermatology">Dermatology</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/dentistry">Dentistry</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/ophthalmology">Ophthalmology</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/physiotherapy">Physiotherapy</Link></li>
                <li className="hover:text-primary transition-colors cursor-pointer"><Link to="/boarding">Pet Boarding</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/orthopaedics">Orthopaedics</Link></li>
             </ul>
          </div>

          <div className="space-y-10 pt-4">
             <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-primary">Diagnostics</h4>
             <ul className="space-y-4 text-white/90 text-sm font-bold">
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/xray">Digital Radiology</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/surgeries">Surgical Referrals</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/gynecology">Obstetrics Unit</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/diagnostic-services">Laboratory Services</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/diagnostics">Medical Records</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/pharmacy">Emergent Pharmacy</Link></li>
                <li className="hover:text-white transition-colors cursor-pointer"><Link to="/critical-care">Ambulance Service</Link></li>
             </ul>
          </div>
        </div>

        <div className="footer-reveal mt-20 pt-16 pb-20 px-8 md:px-16 bg-white/[0.02] rounded-[4rem] border border-white/5 relative overflow-hidden group/network">
           <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover/network:opacity-100 transition-opacity duration-1000 pointer-events-none" />
           
           <div className="flex flex-col items-center text-center space-y-4 mb-12 relative z-10">
              <h3 className="font-display font-medium text-white tracking-widest text-sm uppercase opacity-90">Local Service Areas</h3>
              <p className="text-white/40 text-xs max-w-lg leading-relaxed">If you’re looking for a reliable pet hospital near <strong>Vanasthalipuram, LB Nagar, Hayathnagar, or BN Reddy Nagar</strong>, we are here to provide trusted care for your pets.</p>
              <div className="w-24 h-1 bg-primary/20 rounded-full"></div>
           </div>
           
           <div className="grid md:grid-cols-3 gap-8 lg:gap-10 relative z-10">
              {[
                { 
                  name: 'Vanasthalipuram (Main)', 
                  addr: 'Beside Line Of Vivekanandha Park, Hyderabad, 500070', 
                  hours: '09:00 AM — 09:00 PM (Everyday)',
                  mapUrl: 'https://maps.app.goo.gl/1KD7SS8UeyVASEJq9'
                },
                { 
                  name: 'SR Pet clinic, Hasthinapuram', 
                  addr: 'Hasthinapuram Central, Hyderabad', 
                  hours: '09:30 AM — 09:00 PM',
                  mapUrl: 'https://maps.app.goo.gl/AitGp4EtcSMpVQQd9'
                },
                { 
                  name: 'Pet Planet Dog Clinic, Turkayamjal', 
                  addr: 'Turkayamjal, Hyderabad', 
                  hours: '10:00 AM — 08:30 PM',
                  mapUrl: 'https://maps.app.goo.gl/AngNqHp4njfe8crS6'
                }
              ].map((loc, i) => (
                <div key={i} className="group bg-white/[0.03] backdrop-blur-3xl border border-white/10 p-10 lg:p-12 rounded-[3.5rem] transition-all duration-700 hover:bg-white/[0.08] hover:-translate-y-4 shadow-glass-heavy relative overflow-hidden flex flex-col h-full">
                   <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/20 rounded-full blur-[40px] group-hover:bg-primary/30 transition-all duration-1000 -z-10"></div>
                   
                   <h4 className="font-bold text-2xl mb-8 text-white font-display border-b border-white/10 pb-4 group-hover:text-primary transition-colors">{loc.name}</h4>
                   <div className="flex gap-5 text-white/90 text-sm font-bold leading-relaxed mb-6">
                      <MapPin size={24} className="shrink-0 text-primary opacity-90 mt-1" />
                      <p>{loc.addr}</p>
                   </div>
                   <div className="flex gap-5 text-white/80 text-xs font-black tracking-widest uppercase mb-10 mt-auto">
                      <Clock size={22} className="shrink-0 text-primary opacity-90" />
                      <p>{loc.hours}</p>
                   </div>

                   {loc.mapUrl ? (
                     <a href={loc.mapUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary font-black tracking-[0.15em] text-[12px] uppercase group/btn hover:gap-4 transition-all duration-500">
                        GET DIRECTIONS <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                     </a>
                   ) : (
                     <Link to="/contact" className="inline-flex items-center gap-2 text-primary font-black tracking-[0.15em] text-[12px] uppercase group/btn hover:gap-4 transition-all duration-500">
                        GET DIRECTIONS <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                     </Link>
                   )}
                </div>
              ))}
           </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;